import { createContext, useContext, useState, useRef, useEffect, useCallback, useMemo, type ReactNode } from 'react'
import { useMeeting } from './MeetingContext'
import { useSessaoCaptura } from '../hooks/useSessaoCaptura'
import { toast } from 'sonner'
import ContextoSelectorModal from '../components/captura/ContextoSelectorModal'

const SessaoCapturaCtx = createContext(null)

export function SessaoCapturaProvider({ children }: { children: ReactNode }) {
  const { meeting } = useMeeting()
  const {
    sessaoAtiva, items, loading,
    iniciarSessao:     _iniciar,
    encerrarSessao,
    descartarSessao,
    recuperarSessao,
    adicionarNota,
    adicionarNotaBackground,
    atualizarNota,
    adicionarLegendaVoz,
    deletarNota,
    changeProjeto,
  } = useSessaoCaptura()

  const [expanded, setExpanded] = useState(false)
  const [seconds,  setSeconds]  = useState(0)
  const timerRef = useRef(null)

  // State para seleção de contexto
  const [selectorOpen, setSelectorOpen] = useState(false)
  const pendingOptsRef = useRef(null)

  // Timer — arranca do tempo já decorrido ao recuperar sessão
  useEffect(() => {
    if (sessaoAtiva && !sessaoAtiva.encerrado_em) {
      const elapsed = Math.floor((Date.now() - new Date(sessaoAtiva.iniciado_em).getTime()) / 1000)
      setSeconds(elapsed)
      timerRef.current = setInterval(() => setSeconds(s => s + 1), 1000)
    } else {
      clearInterval(timerRef.current)
      setSeconds(0)
    }
    return () => clearInterval(timerRef.current)
  }, [sessaoAtiva?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => clearInterval(timerRef.current), [])

  // ── Ações com guards ──────────────────────────────────────

  const iniciar = useCallback(async (opts = {}) => {
    if (meeting) {
      toast.warning('Encerre a reunião antes de iniciar uma sessão de captura')
      return null
    }
    // Se contexto já definido, inicia direto
    if (opts.contexto) {
      const sessao = await _iniciar(opts)
      if (sessao) setExpanded(true)
      return sessao
    }
    // Se não, mostra selector
    pendingOptsRef.current = opts
    setSelectorOpen(true)
    return null
  }, [meeting, _iniciar])

  const handleContextoSelect = useCallback(async (contexto) => {
    setSelectorOpen(false)
    const opts = { ...(pendingOptsRef.current ?? {}), contexto }
    pendingOptsRef.current = null
    const sessao = await _iniciar(opts)
    if (sessao) setExpanded(true)
  }, [_iniciar])

  const handleContextoCancel = useCallback(() => {
    setSelectorOpen(false)
    pendingOptsRef.current = null
  }, [])

  const encerrar = async (titulo) => {
    const resumo = await encerrarSessao(titulo)
    setExpanded(false)
    return resumo
  }

  const descartar = async () => {
    await descartarSessao()
    setExpanded(false)
  }

  const expand   = useCallback(() => setExpanded(true), [])
  const minimize = useCallback(() => setExpanded(false), [])

  const value = useMemo(() => ({
    sessao:    sessaoAtiva,
    items,
    loading,
    seconds,
    expanded,
    iniciar,
    encerrar,
    descartar,
    recuperarSessao,
    addNota:              adicionarNota,
    addNotaBackground:    adicionarNotaBackground,
    updateNota:           atualizarNota,
    addLegendaVoz:        adicionarLegendaVoz,
    deleteNota:           deletarNota,
    changeProjeto,
    expand,
    minimize,
  }), [sessaoAtiva, items, loading, seconds, expanded, iniciar, encerrar, descartar,
       recuperarSessao, adicionarNota, adicionarNotaBackground, atualizarNota,
       adicionarLegendaVoz, deletarNota, changeProjeto, expand, minimize])

  return (
    <SessaoCapturaCtx.Provider value={value}>
      {children}
      <ContextoSelectorModal
        open={selectorOpen}
        onSelect={handleContextoSelect}
        onCancel={handleContextoCancel}
      />
    </SessaoCapturaCtx.Provider>
  )
}

export function useSessaoCapturaCtx() {
  const ctx = useContext(SessaoCapturaCtx)
  if (!ctx) throw new Error('useSessaoCapturaCtx fora do SessaoCapturaProvider')
  return ctx
}
